import { Component, Input, OnInit } from '@angular/core';
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import {
  DatePickerFormValidators,
  DatepickerHelper
} from "@shared/validators/date-picker-form-validators";
import { Document } from "@models/documents/document";
import { RelationshipType } from '@models/relationships/relationship-type.enum';
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { DocumentationType, } from "@models/documents/document-enums";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { forkJoin } from "rxjs";
import { IParsedFullName, parseFullName } from "@shared/utils/parse-fullname";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import {
  directorFirstNameValidatorMsg,
  directorFullNameValidatorMsg
} from "@shared/validators/custom-form-validators-messages";
import { CompanyChangePartyName } from '../../../models/company';
import {
  BaseBulkStepperFormComponent,
  StepperFormComponent,
  StepperFormDescriptionComponent
} from "../../stepper-form";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { ParsedName } from "@models/individuals/parsed-name";
import { Company } from "@models/companies/company";
import { BulkCompanyChangePartyNameStepsEnum } from "./bulk-company-change-party-name-steps.enum";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { getBulkCompanyChangePartyNameDocumentSelection } from "./bulk-company-change-party-name-document-selection";
import { Guid } from "@shared/utils/guid.helper";
import { IBulkAppliedChange } from "../base-bulk-stepper-form/base-bulk-stepper-form.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";

@Component({
  selector: 'app-bulk-company-change-party-name',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatePickerComponent,
    InputComponent,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    RadioComponent,
    DisclaimerComponent,
    ListGridClientSideComponent
  ],
  providers: [],
  templateUrl: './bulk-company-change-party-name.component.html',
  styleUrls: ['../../stepper-form/base-stepper-component/base-stepper-form.component.scss']
})
export class BulkCompanyChangePartyNameComponent extends BaseBulkStepperFormComponent<BulkCompanyChangePartyNameStepsEnum, CompanyChangePartyName> implements OnInit {
  @Input() companiesIds: string[] = [];
  @Input() relationshipIds: string[] = [];
  @Input() trustOnly = false;

  override StepsEnum = BulkCompanyChangePartyNameStepsEnum;

  readonly RelationshipType = RelationshipType;
  readonly directorFullNameValidatorMsg = directorFullNameValidatorMsg;
  readonly directorFirstNameValidatorMsg = directorFirstNameValidatorMsg;

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;
  showNameParts = false;

  form = new FormGroup({
    changeDate: new FormControl<Date | null>(DatepickerHelper.getToday(), [Validators.required, DatePickerFormValidators.dateFormatValidator]),
    name: new FormControl<string | null>(null, [Validators.required]),
    newFullName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.fullNameValidator]),
    newName: new FormGroup({
      firstName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.directorFirstNameValidator]),
      middleName1: new FormControl<string | null>(null, [CustomFormValidators.directorFirstNameValidator]),
      middleName2: new FormControl<string | null>(null, [CustomFormValidators.directorFirstNameValidator]),
      lastName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.directorFirstNameValidator]),
    }),
    documentationType: new FormControl<DocumentationType>(DocumentationType.Minutes, [Validators.required]),
  });

  override stepperForm = new FormGroup({
    [BulkCompanyChangePartyNameStepsEnum.FormDescription]: new FormGroup({}),
    [BulkCompanyChangePartyNameStepsEnum.CompanyMemberName]: this.form,
    [BulkCompanyChangePartyNameStepsEnum.Summary]: this.form
  });

  constructor() {
    super();
    this.setupSteps(BulkCompanyChangePartyNameStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDefaultMinDateStruct();
    this.maxDate = DatepickerHelper.getTodayStruct();

    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);
    this.setupChange();
    this.loadCompanies();
  }

  loadCompanies(): void {
    this.isLoading.set(true);

    const selectedEntitiesLoadRequests = this.companiesIds
      .map((companyId) => this.loadCompany(companyId));

    forkJoin(selectedEntitiesLoadRequests)
      .subscribe({
        next: (fullCompanies) => {
          this.companies = fullCompanies.filter(Boolean) as Company[];
          this.fillRows(this.companies);
          this.isLoading.set(false);
        }
      });
  }

  onStepChange(stepIndex: BulkCompanyChangePartyNameStepsEnum): void {
    if (stepIndex === BulkCompanyChangePartyNameStepsEnum.Summary) {
      this.fillRowsErrors();
    }
  }

  override afterSubmit(changes: EntityChangeData[]): void {
    this.setupChange(changes[0] as CompanyChangePartyName);
  }

  override setupChange(change: CompanyChangePartyName = this.formModel): void {
    this.relationshipIds = change.relationshipIds;

    const name = [change.firstName, change.middleName1, change.middleName2, change.lastName].filter(Boolean).join(' ');
    this.form.patchValue({ name, ...change });

    this.fillNameParts();
    setControlDisabled(this.form.controls.name);

    if (this.trustOnly) {
      this.form.controls.documentationType.patchValue(DocumentationType.Default);
    }
  }

  override buildDocument(): Document[] | null {
    if (this.areAllRowsHaveErrors) return null;

    const newName = this.form.controls.newName.value as IParsedFullName;
    const documentationType = this.form.controls.documentationType.value ?? DocumentationType.Default;
    const changeDate = this.form.controls.changeDate.value!;
    const changeDescription = `Name changed to ${ this.form.controls.newFullName.value ?? '' }`;

    try {
      return this.companies.map(((company, index) => {
        const relatedRow = this.rows[index];
        const changeAuthorisation = this.getChangeAuthorisation(company, documentationType, relatedRow, changeDate);

        const officersRelationshipIdsSet = company.officers.reduce((collection, officer) => {
          if (officer.relationshipId && Guid.isValidGuid(officer.relationshipId)) {
            collection.add(officer.relationshipId);
          }

          return collection;
        }, new Set<string>);

        const shareholderRelationshipIdsSet = company.securityholders.reduce((collection, shareholder) => {
          if (shareholder.relationshipId && Guid.isValidGuid(shareholder.relationshipId)) {
            collection.add(shareholder.relationshipId);
          }

          return collection;
        }, new Set<string>);

        const relationshipIds = this.relationshipIds.filter((relationshipId) => officersRelationshipIdsSet.has(relationshipId) || shareholderRelationshipIdsSet.has(relationshipId));

        const change = new CompanyChangePartyName({
          ...newName,
          relationshipIds,
          changeDate,
          description: changeDescription,
        });

        changeAuthorisation.documentSelection = getBulkCompanyChangePartyNameDocumentSelection(changeAuthorisation.documentationType);

        return new Document({
          changes: [change],
          changeAuthorisation,
          entityId: company.entityId,
          type: 'c:484',
          documentId: this.companyChangeData?.documentId,
          documentationType
        });
      }));
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  setShowNameParts(show = true): void {
    this.showNameParts = show;
    this.fillNameParts();
  }

  fillNameParts(): void {
    this.form.controls.changeDate.setValue(this.form.controls.changeDate.value);

    if (this.showNameParts) {
      const parsedName = new ParsedName({ ...this.form.controls.newName.value as Partial<ParsedName> });
      this.form.controls.newFullName.patchValue(parsedName.fullNameWithMiddle);
    } else if (this.form.controls.newFullName.value) {
      // filled from newFullName
      const parsedFullName = parseFullName(this.form.controls.newFullName.value);
      this.form.controls.newName.patchValue(parsedFullName, { emitEvent: false });
    }
  }

  // ===== ===== ===== Handle "Change is applied for" section and it's grid ===== ===== =====

  override getRowError(row: IBulkAppliedChange, rowIndex: number): string {
    const company = this.companies[rowIndex];

    const relatedChangeAddressEntityEstablishmentDate = company.dateOfEstablishment
      ? new Date(company.dateOfEstablishment)
      : company.dateOfEstablishment;
    const changeDate = this.form.controls.changeDate.value;

    if (relatedChangeAddressEntityEstablishmentDate && changeDate && changeDate <= relatedChangeAddressEntityEstablishmentDate) {
      return 'Date of change cannot be earlier or same as the company registration date';
    }

    return super.getRowError(row, rowIndex);
  }
}
